import { styled } from '@mui/material/styles';
import {
  Box,
  Button,
  FormControl,
  MenuItem,
  Paper,
  Select,
  Table,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

// ============================================
// Container & Layout
// ============================================
// 좁은 화면(1366 등)에서는 탭·버튼 글자를 꺾지 않고 우측 버튼 묶음을 다음 줄로 내린다.
export const HeaderContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  rowGap: '8px',
  marginTop: '20px',
  marginBottom: '8px',
});

export const LeftSection = styled(Box)({
  display: 'flex',
  alignItems: 'center',
});

export const RightSection = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  flexShrink: 0,
  marginLeft: 'auto',
});

export const SearchLengthContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  color: '#737373',
});

// ============================================
// Member Type Tab Button
// ============================================
const getTabColor = tabType => {
  switch (tabType) {
    case null:
      return '#00838F'; // 전체
    case 'ACTIVE':
      return '#00838F'; // 유효회원
    case 'SCHEDULED':
      return '#3564D6'; // 사용예정
    case 'EXPIRING_SOON':
    case 'LOCKER_EXPIRING':
      return '#C46A00'; // 만료임박, 락커임박
    case 'STOPPED':
      return '#C2403D'; // 중지회원
    case 'UNPAID':
      return '#F49215'; // 미수회원
    case 'EXPIRED':
      return '#8A939C'; // 만료회원
    default:
      return '#00838F';
  }
};

export const MemberTypeTab = styled(Box, {
  shouldForwardProp: prop => !['isActive', 'tabType', 'disabled'].includes(prop),
})(({ isActive, tabType, disabled }) => {
  const color = getTabColor(tabType);
  const base = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '4px',
    height: '40px',
    minWidth: '90px',
    padding: '0 10px',
    whiteSpace: 'nowrap',
    flexShrink: 0,
    transition: 'all 0.2s ease',
  };
  // createBox 검색 집합에 없는 타입 탭은 회색 비활성 (클릭 불가)
  if (disabled) {
    return {
      ...base,
      cursor: 'not-allowed',
      borderBottom: '2px solid transparent',
      color: '#bfbfbf',
      opacity: 0.5,
    };
  }
  return {
    ...base,
    cursor: 'pointer',
    borderBottom: isActive ? `2px solid ${color}` : '2px solid transparent',
    color: isActive ? color : '#595959',
    '&:hover': {
      color: color,
    },
  };
});

export const MemberTypeLabel = styled('span', {
  shouldForwardProp: prop => prop !== 'isActive',
})(({ isActive }) => ({
  fontSize: '13px',
  fontWeight: 600,
}));

export const MemberTypeCount = styled('span', {
  shouldForwardProp: prop => prop !== 'isActive',
})(({ isActive }) => ({
  fontSize: '14px',
  fontWeight: 600,
}));

// ============================================
// Checkbox Options
// ============================================
export const CheckboxOptionGroup = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

export const CheckboxOptionItem = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '5px',
  padding: '6px 12px',
  borderRadius: '6px',
  border: '1px solid #4AB3BC',
  background: '#4AB3BC',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  '& .checkbox-icon': {
    color: '#fff',
    transition: 'color 0.2s ease',
  },
  '&:hover': {
    background: '#4AB3BC',
    '& .checkbox-icon': {
      color: '#fff',
    },
    '& .checkbox-label': {
      color: '#fff',
    },
  },
});

export const CheckboxOptionLabel = styled(Typography)({
  fontSize: '14px',
  fontWeight: '600',
  color: '#fff',
  whiteSpace: 'nowrap',
  transition: 'color 0.2s ease',
});

// ============================================
// Expiry Setting Chip
// ============================================
export const ExpirySettingChip = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '5px',
  minHeight: '35px',
  padding: '6px 12px',
  whiteSpace: 'nowrap',
  flexShrink: 0,
  borderRadius: '6px',
  border: '1px solid #F87171',
  background: '#fff',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  '&:hover': {
    background: '#F87171',
    '& .expiry-icon': {
      color: '#fff',
    },
    '& .expiry-text': {
      color: '#fff',
    },
  },
});

export const ExpirySettingIcon = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#F87171',
  transition: 'color 0.2s ease',
});

export const ExpirySettingText = styled(Typography)({
  fontSize: '13px',
  fontWeight: 500,
  color: '#F87171',
  whiteSpace: 'nowrap',
  transition: 'color 0.2s ease',
});

// ============================================
// Sort Select
// ============================================
export const SortSelect = styled(Select)({
  minWidth: '110px',
  height: '34px',
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#d9d9d9',
  },
  '& .MuiSelect-select': {
    padding: '6px 12px',
    fontSize: '13px',
  },
});

export const TableWrapper = styled(Box)({
  position: 'relative',
  width: '100%',
});

// ============================================
// Buttons
// ============================================
// 임시회원 목록(TemporaryUserList) 등 다른 화면에서도 쓰는 단순 SMS 버튼 — 그대로 유지
export const SMSButton = styled(Button)({
  minWidth: '90px',
  height: '34px',
  padding: '6px 14px',
  whiteSpace: 'nowrap',
  flexShrink: 0,
  borderRadius: '6px',
  border: '1px solid #4AB3BC',
  background: '#fff',
  color: '#4AB3BC',
  fontSize: '13px',
  fontWeight: 500,
  boxShadow: 'none',
  gap: '6px',
  transition: 'background-color 150ms ease, transform 160ms cubic-bezier(0.23, 1, 0.32, 1)',
  '&:hover': {
    background: 'rgba(74, 179, 188, 0.06)',
    color: '#4AB3BC',
    boxShadow: 'none',
  },
  '&:active': {
    transform: 'scale(0.97)',
  },
});

// 문자/앱 알림 통합 버튼(회원 목록 헤더) — 호버 시 MessagingMenuPaper 팝오버로 두 발송 채널을 고른다
export const MessagingButton = styled(Button)({
  minWidth: '150px',
  height: '34px',
  padding: '6px 14px',
  borderRadius: '6px',
  border: '1px solid #4AB3BC',
  background: '#fff',
  color: '#4AB3BC',
  fontSize: '13px',
  fontWeight: 500,
  boxShadow: 'none',
  gap: '6px',
  transition: 'background-color 150ms ease, transform 160ms cubic-bezier(0.23, 1, 0.32, 1)',
  '&:hover': {
    background: 'rgba(74, 179, 188, 0.06)',
    color: '#4AB3BC',
    boxShadow: 'none',
  },
  '&:active': {
    transform: 'scale(0.97)',
  },
});

export const MessagingMenuPaper = styled(Paper)({
  minWidth: '190px',
  marginTop: '4px',
  padding: '6px',
  borderRadius: '8px',
  border: '1px solid #EEE',
  boxShadow: '0px 8px 24px rgba(0,0,0,0.12)',
});

export const MessagingMenuItem = styled(MenuItem)({
  borderRadius: '6px',
  gap: '8px',
  fontSize: '13px',
  fontWeight: 500,
  color: '#333',
  '&:hover': {
    backgroundColor: 'rgba(74, 179, 188, 0.08)',
    color: '#2b7e85',
  },
});

// 회원 그룹 관리 버튼 — SMS 버튼과 동일한 톤의 아웃라인 버튼
export const GroupManageButton = styled(Button)({
  minWidth: '90px',
  height: '34px',
  padding: '6px 14px',
  // 좁은 화면에서 헤더 우측 버튼들이 눌리면 "그룹 관리"가 두 줄로 꺾이던 것 방지
  whiteSpace: 'nowrap',
  flexShrink: 0,
  borderRadius: '6px',
  border: '1px solid #4AB3BC',
  background: '#fff',
  color: '#4AB3BC',
  fontSize: '13px',
  fontWeight: 500,
  boxShadow: 'none',
  gap: '6px',
  transition: 'background-color 150ms ease, transform 160ms cubic-bezier(0.23, 1, 0.32, 1)',
  '&:hover': {
    background: 'rgba(74, 179, 188, 0.06)',
    color: '#4AB3BC',
    boxShadow: 'none',
  },
  '&:active': {
    transform: 'scale(0.97)',
  },
});

// ============================================
// Typography
// ============================================
export const ResultText = styled(Typography)({
  marginRight: '16px',
});

// ============================================
// Form Controls
// ============================================
export const LimitFormControl = styled(FormControl)({});

export const LimitSelect = styled(Select)({
  minWidth: '90px',
  height: '34px',
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#d9d9d9',
  },
  '& .MuiSelect-select': {
    padding: '6px 12px',
    fontSize: '13px',
  },
});

// ============================================
// Table
// ============================================
export const StyledTableContainer = styled(TableContainer)({
  marginTop: '10px',
  border: '1px solid #e8e8e8',
  borderRadius: '8px',
  overflowX: 'auto',
  overflowY: 'auto',
  width: '100%',
  height: 'auto',
  '&::-webkit-scrollbar': {
    width: '6px',
    height: '6px',
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: '#f1f1f1',
    borderRadius: '10px',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: '#c1c1c1',
    borderRadius: '10px',
    '&:hover': {
      backgroundColor: '#a8a8a8',
    },
  },
  '&::-webkit-scrollbar-corner': {
    backgroundColor: '#f1f1f1',
  },
});

export const StyledTable = styled(Table)({
  tableLayout: 'auto',
  // 컬럼 설정으로 열 수가 달라지므로 고정 폭을 두지 않는다. 셀이 nowrap 이라 내용만큼 늘어난다
  minWidth: '100%',
  // collapse 모드에선 sticky 셀 경계에서 테두리가 반씩 갈라져 틈이 보인다
  borderCollapse: 'separate',
  borderSpacing: 0,
});

export const StyledTableHead = styled(TableHead)({
  position: 'sticky',
  top: 0,
  zIndex: 100,
  background: '#F0F2F5',
  // 왼쪽 고정 헤더 셀. 부트스트랩 `.table > :not(caption) > * > *` 가 셀 배경을 투명으로 덮으므로
  // 그보다 높은 특이도로 직접 색을 준다
  '& th.sticky-cell': { backgroundColor: '#F0F2F5' },
});

export const HeaderCell = styled(TableCell)({
  fontWeight: 600,
  fontSize: '13px',
  color: '#333',
  whiteSpace: 'nowrap',
  padding: '10px 12px',
  borderRight: '1px solid #ddd',
  '&:last-child': {
    borderRight: 'none',
  },
});

export const CheckboxCell = styled(TableCell)({
  width: '15px',
  borderRight: '1px solid #ddd',
});

export const StyledTableRow = styled(TableRow, {
  shouldForwardProp: prop => prop !== 'expired',
})(({ expired }) => ({
  textDecoration: 'none',
  height: '52px',
  borderBottom: '1px solid #eee',
  // 왼쪽 고정(sticky) 셀 배경. 행 배경은 부트스트랩 `.table` 규칙이 투명으로 덮어 inherit 로는 못 받으므로
  // 행 상태(기본/만료/hover)별로 셀에 직접 준다
  '& td.sticky-cell': { backgroundColor: expired ? '#f5f5f5' : '#fff' },
  ...(expired && {
    backgroundColor: '#f5f5f5',
    borderLeft: '3px solid #ccc',
    // 만료 행 데이터 텍스트. #bbb 는 너무 흐려 읽히지 않는다는 피드백으로 검정 전환 (2026-08-21).
    // 배경·좌측 바는 그대로 둬서 만료 행이라는 구분 자체는 유지한다.
    '& td': { color: '#000000' },
  }),
  '&:hover': {
    cursor: 'pointer',
    backgroundColor: expired ? '#f0f0f0' : '#f8f9fa',
    '& td.sticky-cell': { backgroundColor: expired ? '#f0f0f0' : '#f8f9fa' },
  },
}));

export const BodyCell = styled(TableCell)({
  fontSize: '13px',
  color: '#333',
  textDecoration: 'none',
  whiteSpace: 'nowrap',
  padding: '8px 12px',
  borderRight: '1px solid #f0f0f0',
  '&:last-child': { borderRight: 'none' },
});

export const BodyCellNoWrap = styled(TableCell)({
  fontSize: '13px',
  color: '#333',
  textDecoration: 'none',
  whiteSpace: 'nowrap',
  padding: '8px 12px',
  borderRight: '1px solid #f0f0f0',
  '&:last-child': { borderRight: 'none' },
});

export const CheckboxBodyCell = styled(TableCell)({
  fontSize: '12px',
  fontWeight: 500,
  borderRight: '1px solid #f0f0f0',
});

// ============================================
// Member Status Badge
// ============================================
const getBadgeColor = memberType => {
  switch (memberType) {
    case '유효회원':
      return '#00838F';
    case '사용예정':
      return '#3564D6';
    case '만료임박':
    case '라커만료':
      return '#C46A00';
    case '중지회원':
      return '#C2403D';
    case '미수회원':
      return '#F49215';
    case '만료회원':
      // 회색이라 흐려 보인다는 피드백으로 검정 전환 (2026-08-21). 칩 전체(글자·점·배경·테두리)에
      // 이 값이 쓰인다. default 폴백은 회색 그대로 — 알 수 없는 구분까지 검게 만들 이유가 없다.
      return '#000000';
    default:
      return '#8A939C';
  }
};

export const MemberStatusBadge = styled(Box, {
  shouldForwardProp: prop => prop !== 'memberType',
})(({ memberType }) => {
  const color = getBadgeColor(memberType);
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '4px 12px',
    borderRadius: '20px',
    fontSize: '11.5px',
    fontWeight: 700,
    letterSpacing: '0.3px',
    backgroundColor: `${color}18`,
    border: `1px solid ${color}40`,
    color: color,
    whiteSpace: 'nowrap',
    '&::before': {
      content: '""',
      width: '6px',
      height: '6px',
      borderRadius: '50%',
      backgroundColor: color,
      flexShrink: 0,
    },
  };
});

// ============================================
// Ticket Info
// ============================================
export const TicketInfoText = styled('p')({
  fontSize: '0.875rem',
  marginBottom: '3px',
  margin: 0,
});

export const SuspendedText = styled('span', {
  shouldForwardProp: prop => prop !== 'isSuspended',
})(({ isSuspended }) => ({
  color: isSuspended ? '#ff0000' : 'inherit',
  fontSize: '14px',
}));

// ============================================
// Loading Overlay
// ============================================
export const TableLoadingOverlay = styled(Box)({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(255, 255, 255, 0.7)',
  backdropFilter: 'blur(2px)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 10,
});

// ============================================
// 하단 중앙 플로팅 액션 바 (회원 선택 시)
// ============================================
export const FloatingActionBar = styled(Box)({
  position: 'fixed',
  bottom: '32px',
  left: '50%',
  transform: 'translateX(-50%)',
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
  padding: '12px 16px 12px 24px',
  borderRadius: '12px',
  backgroundColor: '#fff',
  boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.18)',
  border: '1px solid #EEE',
  zIndex: 1200,
});

// 드래그 손잡이(그립). 바 배경 어디를 잡아도 움직이지만, 이동 가능함을 시각적으로 알린다.
export const FloatingBarGrip = styled('span')({
  cursor: 'move',
  color: '#C4C9D0',
  fontSize: '16px',
  lineHeight: 1,
  userSelect: 'none',
  marginRight: '-4px',
});

export const FloatingBarInfo = styled(Typography)({
  fontSize: '14px',
  fontWeight: 600,
  color: '#333',
  whiteSpace: 'nowrap',
});

export const FloatingDeleteButton = styled(Button)({
  minWidth: '80px',
  height: '34px',
  padding: '6px 14px',
  borderRadius: '6px',
  border: '1px solid #F44336',
  background: '#fff',
  color: '#F44336',
  fontSize: '13px',
  fontWeight: 500,
  boxShadow: 'none',
  gap: '6px',
  transition: 'background-color 150ms ease, transform 160ms cubic-bezier(0.23, 1, 0.32, 1)',
  '&:hover': {
    background: 'rgba(244, 67, 54, 0.06)',
    color: '#F44336',
    boxShadow: 'none',
  },
  '&:active': {
    transform: 'scale(0.97)',
  },
});

export const FloatingCloseButton = styled(Button)({
  minWidth: '32px',
  width: '32px',
  height: '32px',
  padding: 0,
  borderRadius: '6px',
  background: 'transparent',
  color: '#999',
  fontSize: '18px',
  lineHeight: 1,
  boxShadow: 'none',
  transition: 'background-color 150ms ease, transform 160ms cubic-bezier(0.23, 1, 0.32, 1)',
  '&:hover': {
    background: '#F5F5F5',
    boxShadow: 'none',
  },
  '&:active': {
    transform: 'scale(0.97)',
  },
});

// 선택 회원을 그룹에 담는 버튼. 삭제 버튼과 같은 크기·모양에 색만 브랜드 톤으로 둔다.
export const FloatingGroupAddButton = styled(Button)({
  minWidth: '96px',
  height: '34px',
  padding: '6px 14px',
  borderRadius: '6px',
  border: '1px solid #4AB3BC',
  background: '#fff',
  color: '#4AB3BC',
  fontSize: '13px',
  fontWeight: 500,
  boxShadow: 'none',
  gap: '6px',
  transition: 'background-color 150ms ease, transform 160ms cubic-bezier(0.23, 1, 0.32, 1)',
  '&:hover': {
    background: 'rgba(74, 179, 188, 0.06)',
    color: '#4AB3BC',
    boxShadow: 'none',
  },
  '&:active': {
    transform: 'scale(0.97)',
  },
});

